import { supabase } from '../../lib/supabase';

// Cache to prevent multiple simultaneous calls for the same user
const pendingGuardianFetches = new Map();

/**
 * Fetch guardian profile from the database
 * @param {string} userId - The authenticated user's ID
 * @returns {Promise<{data: any, error: any}>} - Result of the profile fetch
 */
export const fetchGuardianProfile = async (userId) => {
  // Check if there's already a pending fetch for this user
  if (pendingGuardianFetches.has(userId)) {
    // eslint-disable-next-line no-console
    console.log('🔄 Guardian profile fetch already in progress, waiting...');
    return pendingGuardianFetches.get(userId);
  }

  // Create a promise for this fetch and cache it
  const fetchPromise = (async () => {
    try {
      const { data, error } = await supabase
        .from('guardians')
        .select('*')
        .eq('auth_user_id', userId)
        .single();

      // If no profile exists, create one using the edge function
      if (error && error.code === 'PGRST116') {
        // eslint-disable-next-line no-console
        console.log('🔄 No guardian profile found, creating new profile...');

        try {
          const { data: createData, error: createError } =
            await supabase.functions.invoke('create-guardian-record', {
              body: {},
            });

          if (createError) {
            console.error('❌ Error creating guardian profile:', createError);
            return { data: null, error: createError };
          }

          // eslint-disable-next-line no-console
          console.log('✅ Guardian profile created successfully:', createData);

          // After creating, fetch the actual guardian record from the database
          // to ensure we return the same format as the regular fetch
          const { data: fetchedGuardian, error: fetchError } = await supabase
            .from('guardians')
            .select('*')
            .eq('auth_user_id', userId)
            .single();

          if (fetchError) {
            console.error(
              '❌ Error fetching newly created guardian profile:',
              fetchError
            );
            return { data: null, error: fetchError };
          }

          // eslint-disable-next-line no-console
          console.log(
            '✅ Newly created guardian profile fetched successfully:',
            fetchedGuardian
          );
          return { data: fetchedGuardian, error: null };
        } catch (createException) {
          console.error(
            '💥 Exception caught in create guardian profile:',
            createException
          );
          return { data: null, error: createException };
        }
      }

      if (error) {
        console.error('❌ Error fetching guardian profile:', error);
        return { data: null, error };
      }

      // eslint-disable-next-line no-console
      console.log('✅ Guardian profile fetched successfully:', data);
      return { data, error: null };
    } catch (error) {
      console.error('💥 Exception caught in fetchGuardianProfile:', error);
      return { data: null, error };
    } finally {
      // Always clean up the cache when done
      pendingGuardianFetches.delete(userId);
    }
  })();

  // Cache the promise
  pendingGuardianFetches.set(userId, fetchPromise);

  return fetchPromise;
};

/**
 * Fetch all children associated with the current guardian
 * @returns {Promise<{data: any[], error: any}>} - Result of the children fetch
 */
export const fetchGuardianChildren = async () => {
  try {
    const { data, error } = await supabase
      .from('guardian_children')
      .select(
        `
        child_id,
        guardians:guardian_id (
          guardian_code
        ),
        children:child_id (
          id,
          name,
          username,
          password,
          school_year,
          created_at
        )
      `
      )
      .order('created_at', { ascending: false });

    if (error) {
      console.error('❌ Error fetching guardian children:', error);
      return { data: [], error };
    }

    // Transform the data to include guardian code with each child
    const children = data
      .map((item) => ({
        ...item.children,
        guardian_code: item.guardians?.guardian_code,
      }))
      .filter((child) => child.id !== null);

    // eslint-disable-next-line no-console
    console.log('✅ Guardian children fetched successfully:', children);
    return { data: children, error: null };
  } catch (error) {
    console.error('💥 Exception caught in fetchGuardianChildren:', error);
    return { data: [], error };
  }
};

/**
 * Update a child's school year
 * @param {string} childId - The UUID of the child to update
 * @param {number} schoolYear - The new school year (0 for Reception, 1-6 for Years 1-6)
 * @returns {Promise<{data: any, error: any}>} - Result of the update operation
 */
export const updateChildSchoolYear = async (childId, schoolYear) => {
  try {
    const { data, error } = await supabase
      .from('children')
      .update({ school_year: schoolYear })
      .eq('id', childId)
      .select()
      .single();

    if (error) {
      console.error('❌ Error updating child school year:', error);
      return { data: null, error };
    }

    // eslint-disable-next-line no-console
    console.log('✅ Child school year updated successfully:', data);
    return { data, error: null };
  } catch (error) {
    console.error('💥 Exception caught in updateChildSchoolYear:', error);
    return { data: null, error };
  }
};

/**
 * Create a child account using Supabase Edge Function
 * @param {string} username - The username for the child account
 * @param {number} schoolYear - The school year (0 for Reception, 1-6 for Years 1-6)
 * @returns {Promise<{data: any, error: any}>} - Result of the account creation
 */
export const createChildAccount = async (username, schoolYear) => {
  const requestPayload = {
    child_username: username.trim().toLowerCase(),
    school_year: schoolYear,
  };

  // eslint-disable-next-line no-console
  console.log('🚀 Creating child account with payload:', requestPayload);
  // eslint-disable-next-line no-console
  console.log('📡 Supabase URL:', supabase.supabaseUrl);
  // eslint-disable-next-line no-console
  console.log(
    '🔑 Using anon key:',
    supabase.supabaseKey ? '✅ Present' : '❌ Missing'
  );

  try {
    // eslint-disable-next-line no-console
    console.log('📤 Invoking Supabase function: create-child-account');

    const { data, error } = await supabase.functions.invoke(
      'create-child-account',
      {
        body: requestPayload,
      }
    );

    // eslint-disable-next-line no-console
    console.log('📥 Function response received:');
    // eslint-disable-next-line no-console
    console.log('  - Data:', data);
    // eslint-disable-next-line no-console
    console.log('  - Error:', error);

    if (error) {
      console.error('❌ Function returned error:', {
        message: error.message,
        status: error.status,
        statusText: error.statusText,
        details: error.details || error,
      });
      throw error;
    }

    // eslint-disable-next-line no-console
    console.log('✅ Child account created successfully:', data);
    return { data, error: null };
  } catch (error) {
    console.error('💥 Exception caught in createChildAccount:', {
      name: error.name,
      message: error.message,
      status: error.status,
      statusText: error.statusText,
      stack: error.stack,
      fullError: error,
    });
    return { data: null, error };
  }
};
